import { useState, useCallback } from "react";
import { useToast, useDialog } from "@repo/ui";
import type { Item, FieldData, FieldValue } from "../types";
import type { UseFieldMutations, UseTasksInfo } from "./types";

type PendingChange = {
  fieldId: number;
  itemIds: number[];
  oldValues: string[];
  newValue: string;
  isMixed: boolean;
};

type UpdateParams = {
  fields: { fieldId: number; value: string }[];
};

type UpdateTaskParams = UpdateParams & {
  taskId: number;
};

type UpdateItemParams = UpdateParams & {
  itemId: number;
};

type Props = {
  items: Item[];
  itemFields: FieldData[];
  windowId?: string;
  useFieldMutations: UseFieldMutations;
  useTasksInfo: UseTasksInfo;
};

export const useItemFieldEditing = ({
  items,
  itemFields,
  windowId,
  useFieldMutations,
  useTasksInfo,
}: Props) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, FieldValue>>({});
  const [pendingChanges, setPendingChanges] = useState<Record<number, PendingChange>>({});
  const { showToast } = useToast();
  const { showDialog, closeDialog } = useDialog();

  const tasksInfo = useTasksInfo(items);

  const handleMutationSuccess = useCallback((fieldIds: number[]) => {
    setPendingChanges((prev) => {
      const newPending = { ...prev };
      fieldIds.forEach((id) => delete newPending[id]);
      return newPending;
    });

    setEditValues((prev) => {
      const newEditValues = { ...prev };
      fieldIds.forEach((id) => delete newEditValues[id]);
      return newEditValues;
    });
  }, []);

  const { updateTaskMutation, updateItemMutation, isSaving } = useFieldMutations({
    items,
    onSuccess: handleMutationSuccess,
    windowId,
  });

  const handleEditClick = useCallback((fieldId: number, currentValue: FieldValue) => {
    setEditingField(String(fieldId));
    setEditValues((prev) => ({ ...prev, [fieldId]: currentValue }));
  }, []);

  const handleClearClick = useCallback(
    (fieldId: number) => {
      const field = itemFields.find((f) => f.id === fieldId);
      if (!field) return;

      setPendingChanges((prev) => ({
        ...prev,
        [fieldId]: {
          fieldId,
          itemIds: items.map((item) => item.id),
          oldValues: field.values,
          newValue: "~",
          isMixed: field.isMixed,
        },
      }));
    },
    [itemFields, items]
  );

  const handleSaveClick = useCallback(
    (fieldId: number) => {
      const newValue = editValues[fieldId];
      const field = itemFields.find((f) => f.id === fieldId);

      if (!field) {
        setEditingField(null);
        return;
      }

      const originalValues = field.values;

      let allValuesMatch = false;
      if (originalValues.length === 0) {
        allValuesMatch = newValue === "" || newValue === undefined || newValue === null;
      } else {
        allValuesMatch = originalValues.every((v) => v === newValue);
      }

      if (allValuesMatch) {
        setEditingField(null);

        setPendingChanges((prev) => {
          if (prev[fieldId]) {
            const newPending = { ...prev };
            delete newPending[fieldId];
            return newPending;
          }
          return prev;
        });

        setEditValues((prev) => {
          const newEditValues = { ...prev };
          delete newEditValues[fieldId];
          return newEditValues;
        });

        return;
      }

      setPendingChanges((prev) => ({
        ...prev,
        [fieldId]: {
          fieldId,
          itemIds: items.map((item) => item.id),
          oldValues: originalValues,
          newValue: String(newValue),
          isMixed: field.isMixed,
        },
      }));

      setEditingField(null);
    },
    [editValues, itemFields, items]
  );

  const handleCancelClick = useCallback(() => {
    const currentFieldId = editingField ? parseInt(editingField) : null;
    if (currentFieldId && editValues[currentFieldId]) {
      setEditValues((prev) => {
        const newEditValues = { ...prev };
        delete newEditValues[currentFieldId];
        return newEditValues;
      });
    }
    setEditingField(null);
  }, [editingField, editValues]);

  const handleInputChange = useCallback((fieldId: number, value: FieldValue) => {
    setEditValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const executeSave = useCallback(
    async (changesList: PendingChange[]) => {
      try {
        const taskUpdatesMap: Record<number, { fieldId: number; value: string }[]> = {};
        const itemUpdatesMap: Record<number, { fieldId: number; value: string }[]> = {};

        changesList.forEach((change) => {
          const affectedTasks = tasksInfo.filter(
            (task) =>
              task.allItemsSelected &&
              change.itemIds.every((itemId) => task.selectedItemIds.includes(itemId))
          );

          if (affectedTasks.length > 0) {
            affectedTasks.forEach((task) => {
              if (!taskUpdatesMap[task.taskId]) taskUpdatesMap[task.taskId] = [];
              taskUpdatesMap[task.taskId]!.push({
                fieldId: change.fieldId,
                value: change.newValue,
              });
            });
          } else {
            change.itemIds.forEach((itemId) => {
              if (!itemUpdatesMap[itemId]) itemUpdatesMap[itemId] = [];
              itemUpdatesMap[itemId]!.push({
                fieldId: change.fieldId,
                value: change.newValue,
              });
            });
          }
        });

        const taskApiUpdates: UpdateTaskParams[] = Object.entries(taskUpdatesMap).map(
          ([taskId, fields]) => ({
            taskId: Number(taskId),
            fields,
          })
        );

        const itemApiUpdates: UpdateItemParams[] = Object.entries(itemUpdatesMap).map(
          ([itemId, fields]) => ({
            itemId: Number(itemId),
            fields,
          })
        );

        await Promise.all([
          ...taskApiUpdates.map((update) => updateTaskMutation.mutateAsync(update)),
          ...itemApiUpdates.map((update) => updateItemMutation.mutateAsync(update)),
        ]);

        showToast("success", "Success", "All changes saved successfully");
        setPendingChanges({});
        setEditValues({});
      } catch (_error) {
        showToast("error", "Error", "Some changes failed to save");
      }
    },
    [tasksInfo, updateTaskMutation, updateItemMutation, showToast]
  );

  const handleSaveAllChanges = useCallback(async () => {
    const changesList = Object.values(pendingChanges);

    if (changesList.length === 0) return;

    const hasMixedValuesChange = changesList.some((change) => change.isMixed);

    if (hasMixedValuesChange) {
      showDialog({
        title: "Confirm Update",
        description:
          "You are about to update fields that have different values across selected items. Doing so will replace all their values with the one you provided. Are you sure you want to continue?",
        confirmText: "Yes, Update All",
        cancelText: "Go back",
        cancelVariant: "outline",
        onConfirm: async () => {
          await executeSave(changesList);
          closeDialog();
        },
      });
      return;
    }

    await executeSave(changesList);
  }, [pendingChanges, showDialog, executeSave, closeDialog]);

  const discardAllChanges = useCallback(() => {
    setPendingChanges({});
    setEditValues({});
    showToast("info", "Discarded", "All changes have been discarded");
  }, [showToast]);

  return {
    editingField,
    editValues,
    pendingChanges,
    isSaving,

    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleInputChange,
    handleSaveAllChanges,
    discardAllChanges,
    handleClearClick,
  };
};
